<script>
import Graph from 'graphology';
import Sigma from 'sigma';
import { drawDiscNodeLabel } from 'sigma/rendering';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import { mapActions, mapState } from 'vuex';
import LoadingState from '../../../../../components/widgets/LoadingState.vue';

// Deterministic categorical palette — `type` is an open-ended set returned by
// the API (artifact/content/method/event/concept/location/organization/
// person/...), so colors are derived by hashing the type string into this
// fixed palette rather than hardcoding a type list.
const PALETTE = [
  '#1976D2',
  '#D32F2F',
  '#388E3C',
  '#F57C00',
  '#7B1FA2',
  '#0097A7',
  '#C2185B',
  '#5D4037',
  '#455A64',
  '#AFB42B',
  '#00796B',
  '#E64A19',
];
// A light dimmed color reads as "brighter", not dimmer, against a dark
// canvas — needs its own dark-mode value so dimming still recedes visually.
const DIMMED_COLOR_LIGHT = '#cbd5e1';
const DIMMED_COLOR_DARK = '#334155';

const colorForType = type => {
  let hash = 0;
  const str = type || 'unknown';
  for (let i = 0; i < str.length; i += 1) {
    hash = Math.abs(hash * 31 + str.charCodeAt(i));
  }
  return PALETTE[hash % PALETTE.length];
};

// ForceAtlas2 pushes node coordinates well outside the [0,1] range Sigma's
// default camera (x:0.5, y:0.5, ratio:1) looks at — without this the graph
// is fully laid out and rendered, just entirely outside the visible
// viewport. Rescale positions back to fit the default camera after layout.
const normalizePositions = graph => {
  const nodes = graph.nodes();
  if (nodes.length === 0) return;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  nodes.forEach(node => {
    const { x, y } = graph.getNodeAttributes(node);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  });
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  // 1.15x padding so hub nodes at the edge of the layout aren't clipped
  // right at the viewport boundary.
  const scale = (Math.max(maxX - minX, maxY - minY) || 1) * 1.15;
  nodes.forEach(node => {
    const { x, y } = graph.getNodeAttributes(node);
    // Center at (0.5, 0.5) — Sigma's default camera looks at that point.
    graph.setNodeAttribute(node, 'x', (x - centerX) / scale + 0.5);
    graph.setNodeAttribute(node, 'y', (y - centerY) / scale + 0.5);
  });
};

const nodeSize = degree =>
  Math.max(4, Math.min(22, 4 + Math.sqrt(degree || 0) * 3.2));
const edgeSize = weight =>
  Math.max(0.5, Math.min(3.2, 0.6 + Math.sqrt(weight || 1) * 0.55));
// How much of a dragged node's movement its direct neighbors inherit each
// frame — a cheap stand-in for the "drag pulls nearby nodes along" look
// tools like Neo4j Browser get for free from a live d3-force simulation
// (forceLink reacting every tick to the dragged node's pinned position).
// We only run layout once (ForceAtlas2, on load), so there's no live
// simulation for a link force to act within — this just nudges immediate
// neighbors by a fraction of the same delta instead.
const NEIGHBOR_DRAG_PULL = 0.35;

// Sigma draws directly to canvas — it has no idea about the app's Tailwind
// dark: classes, so label/edge colors need to be set explicitly or they
// silently keep Sigma's own hardcoded defaults regardless of theme.
const EDGE_COLOR_LIGHT = '#cbd5e1';
const EDGE_COLOR_DARK = '#475569';
const LABEL_COLOR_LIGHT = '#1e293b';
const LABEL_COLOR_DARK = '#e2e8f0';
const HOVER_BG_LIGHT = '#ffffff';
const HOVER_BG_DARK = '#1e293b';

// Read once at render time — this page is freshly mounted per visit, so a
// live-updating MutationObserver isn't warranted for a theme toggled mid-view.
const isBodyInDarkMode = () =>
  typeof document !== 'undefined' && document.body.classList.contains('dark');

// Sigma's built-in hover renderer hardcodes a white ("#FFF") background box
// behind the label with no settings-level way to change it (a plain color
// setting like labelColor doesn't exist for this — confirmed via sigma.js
// issue #1210). This is a faithful copy of sigma's own drawDiscNodeHover
// (packages/sigma/src/rendering/node-hover.ts), swapping only that one
// hardcoded fillStyle for a theme-aware color; the label text itself already
// reuses sigma's real drawDiscNodeLabel, which already respects our
// labelColor setting.
const drawThemedNodeHover = (context, data, settings) => {
  const { labelSize: size, labelFont: font, labelWeight: weight } = settings;
  context.font = `${weight} ${size}px ${font}`;

  context.fillStyle = isBodyInDarkMode() ? HOVER_BG_DARK : HOVER_BG_LIGHT;
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 8;
  context.shadowColor = '#000';

  const PADDING = 2;

  if (typeof data.label === 'string') {
    const textWidth = context.measureText(data.label).width;
    const boxWidth = Math.round(textWidth + 5);
    const boxHeight = Math.round(size + 2 * PADDING);
    const radius = Math.max(data.size, size / 2) + PADDING;

    const angleRadian = Math.asin(boxHeight / 2 / radius);
    const xDeltaCoord = Math.sqrt(
      Math.abs(radius ** 2 - (boxHeight / 2) ** 2)
    );

    context.beginPath();
    context.moveTo(data.x + xDeltaCoord, data.y + boxHeight / 2);
    context.lineTo(data.x + radius + boxWidth, data.y + boxHeight / 2);
    context.lineTo(data.x + radius + boxWidth, data.y - boxHeight / 2);
    context.lineTo(data.x + xDeltaCoord, data.y - boxHeight / 2);
    context.arc(data.x, data.y, radius, angleRadian, -angleRadian);
    context.closePath();
    context.fill();
  } else {
    context.beginPath();
    context.arc(data.x, data.y, data.size + PADDING, 0, Math.PI * 2);
    context.closePath();
    context.fill();
  }

  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 0;

  drawDiscNodeLabel(context, data, settings);
};

// Splits a description on the literal `<SEP>` marker the API uses to join
// multiple source mentions of the same entity — never show the raw string.
const splitDescription = description => {
  if (!description) return [];
  return description
    .split('<SEP>')
    .map(part => part.trim())
    .filter(Boolean);
};

export default {
  name: 'KnowledgeGraph',
  components: { LoadingState },
  data() {
    return {
      isLoading: true,
      errorType: null, // 'not-lightrag' | 'graph-disabled' | 'not-found' | 'generic' | null
      nodes: [],
      edges: [],
      isTruncated: false,
      searchQuery: '',
      depth: 3,
      maxNodes: 1000,
      findInViewQuery: '',
      selectedNodeId: null,
      dimmedTypes: [],
      isFullscreen: false,
      panelWidth: 320,
      // Bumped on every loadGraph() call so an in-flight request can tell,
      // once its response lands, whether it's still the latest one — search,
      // clear, and neighbor navigation all funnel through loadGraph with no
      // network-level cancellation, so responses can resolve out of order.
      graphRequestId: 0,
    };
  },
  computed: {
    ...mapState('knowledge', ['knowledgeDetails']),
    datasetId() {
      return this.$route.params.id;
    },
    legend() {
      const counts = {};
      this.nodes.forEach(node => {
        const type = node.type || 'unknown';
        counts[type] = (counts[type] || 0) + 1;
      });
      return Object.keys(counts)
        .sort()
        .map(type => ({
          type,
          color: colorForType(type),
          count: counts[type],
        }));
    },
    selectedNode() {
      if (
        !this.selectedNodeId ||
        !this.graph ||
        !this.graph.hasNode(this.selectedNodeId)
      ) {
        return null;
      }
      const attrs = this.graph.getNodeAttributes(this.selectedNodeId);
      const neighbors = this.graph
        .neighbors(this.selectedNodeId)
        .map(neighborId => this.graph.getNodeAttributes(neighborId).label);
      return {
        id: this.selectedNodeId,
        label: attrs.label,
        type: attrs.entityType,
        descriptionLines: attrs.descriptionLines,
        sourceIds: attrs.sourceIds,
        neighbors,
      };
    },
  },
  methods: {
    ...mapActions('knowledge', ['fetchDatasetDetails', 'fetchKnowledgeGraph']),
    splitDescription,
    async loadGraph(label) {
      this.graphRequestId += 1;
      const requestId = this.graphRequestId;
      this.destroyRenderer();
      this.isLoading = true;
      this.errorType = null;
      this.selectedNodeId = null;
      this.findInViewQuery = '';
      this.dimmedTypes = [];
      let builtGraph = null;
      try {
        const data = await this.fetchKnowledgeGraph({
          datasetId: this.datasetId,
          label: label || '*',
          depth: this.depth,
          max: this.maxNodes,
        });
        // A newer loadGraph() call has since started — this response is
        // stale, so applying it would render an older search over the
        // newer one's (possibly still in-flight) results.
        if (requestId !== this.graphRequestId) return;
        this.nodes = data.nodes || [];
        this.edges = data.edges || [];
        this.isTruncated = !!data.is_truncated;
        builtGraph = this.buildGraph();
      } catch (err) {
        if (requestId !== this.graphRequestId) return;
        if (err.response?.status === 404) {
          this.errorType = 'not-found';
        } else {
          this.errorType = 'generic';
        }
        console.error('Error loading knowledge graph:', err);
      } finally {
        if (requestId === this.graphRequestId) {
          this.isLoading = false;
        }
      }
      // isLoading=false above only *schedules* Vue's re-render into the
      // v-else (graph canvas) branch — it hasn't happened yet at this point
      // in the same synchronous tick. Wait for a nextTick registered AFTER
      // that flip, so the DOM has actually patched into that branch (and
      // $refs.sigmaContainer actually exists) before touching Sigma.
      if (builtGraph) {
        await this.$nextTick();
        this.renderSigma(builtGraph);
      }
    },
    buildGraph() {
      const graph = new Graph({ type: 'undirected' });
      const edgeColor = isBodyInDarkMode() ? EDGE_COLOR_DARK : EDGE_COLOR_LIGHT;
      this.nodes.forEach(node => {
        graph.addNode(node.id, {
          label: node.label || node.id,
          size: nodeSize(node.degree),
          x: Math.random(),
          y: Math.random(),
          color: colorForType(node.type),
          entityType: node.type,
          descriptionLines: splitDescription(node.description),
          sourceIds: node.source_ids || [],
        });
      });
      this.edges.forEach(edge => {
        // A capped graph (max param) can cut off an edge's far-end node.
        if (!graph.hasNode(edge.source) || !graph.hasNode(edge.target)) return;
        // Dedupe reciprocal A→B / B→A pairs — the graph is undirected.
        if (graph.hasEdge(edge.source, edge.target)) return;
        graph.addEdge(edge.source, edge.target, {
          size: edgeSize(edge.weight),
          color: edgeColor,
        });
      });

      if (graph.order > 0) {
        const settings = forceAtlas2.inferSettings(graph);
        forceAtlas2.assign(graph, {
          iterations: graph.order > 500 ? 150 : 300,
          settings,
        });
        normalizePositions(graph);
      }

      return graph;
    },
    renderSigma(graph) {
      const container = this.$refs.sigmaContainer;
      if (!container) {
        console.error('[Graph.vue] sigmaContainer ref is falsy — aborting render.');
        return;
      }
      this.graph = graph;
      const labelColor = isBodyInDarkMode() ? LABEL_COLOR_DARK : LABEL_COLOR_LIGHT;
      try {
        this.sigmaRenderer = new Sigma(graph, container, {
          labelColor: { color: labelColor },
          defaultDrawNodeHover: drawThemedNodeHover,
        });
      } catch (err) {
        console.error('[Graph.vue] Failed to construct Sigma renderer:', err);
        this.errorType = 'generic';
        return;
      }
      this.sigmaRenderer.on('clickNode', ({ node }) => {
        this.selectedNodeId = node;
      });
      this.sigmaRenderer.on('clickStage', () => {
        this.selectedNodeId = null;
      });

      // Node dragging — Sigma has no built-in "draggable" setting, this is
      // its standard downNode/moveBody/mouseup pattern. Two easy traps in
      // Sigma's own event names here (confirmed by reading its source, not
      // its docs): the MouseCaptor-internal event is "mousemovebody", but
      // the public Sigma instance re-wraps and re-emits it as "moveBody"
      // with a nested `{ event, preventSigmaDefault }` payload — and there
      // is no generic "mouseup" on the public instance at all, only
      // upNode/upEdge/upStage depending what's under the cursor at release.
      // A plain document listener sidesteps that and is more robust anyway
      // (fires even if released outside the canvas entirely).
      this.sigmaRenderer.on('downNode', ({ node }) => {
        this.draggedNode = node;
        graph.setNodeAttribute(node, 'highlighted', true);
        this.sigmaRenderer.getCamera().disable();
      });
      this.sigmaRenderer.on('moveBody', ({ event }) => {
        if (!this.draggedNode) return;
        const pos = this.sigmaRenderer.viewportToGraph(event);
        const dx = pos.x - graph.getNodeAttribute(this.draggedNode, 'x');
        const dy = pos.y - graph.getNodeAttribute(this.draggedNode, 'y');
        graph.setNodeAttribute(this.draggedNode, 'x', pos.x);
        graph.setNodeAttribute(this.draggedNode, 'y', pos.y);
        graph.forEachNeighbor(this.draggedNode, (neighbor, attrs) => {
          graph.setNodeAttribute(
            neighbor,
            'x',
            attrs.x + dx * NEIGHBOR_DRAG_PULL
          );
          graph.setNodeAttribute(
            neighbor,
            'y',
            attrs.y + dy * NEIGHBOR_DRAG_PULL
          );
        });
        event.preventSigmaDefault();
        event.original.preventDefault();
        event.original.stopPropagation();
      });
      this.handleDragEnd = () => {
        if (this.draggedNode) {
          graph.removeNodeAttribute(this.draggedNode, 'highlighted');
        }
        this.draggedNode = null;
        this.sigmaRenderer?.getCamera().enable();
      };
      document.addEventListener('mouseup', this.handleDragEnd);

      // Sigma only re-measures its canvas on the browser's `window` resize
      // event (confirmed in its source) — it has no ResizeObserver of its
      // own. A CSS-only layout shift that isn't a window resize (a
      // scrollbar appearing/disappearing, a flex reflow, etc.) leaves its
      // canvas sized to a stale snapshot of the container, so the graph
      // renders into only a sub-region of the visible box. Watch the real
      // container and keep Sigma in sync regardless of what caused it.
      this.resizeObserver = new ResizeObserver(() => {
        this.sigmaRenderer?.resize();
        this.sigmaRenderer?.refresh();
      });
      this.resizeObserver.observe(container);
    },
    zoomIn() {
      this.sigmaRenderer?.getCamera().animatedZoom({ duration: 200 });
    },
    zoomOut() {
      this.sigmaRenderer?.getCamera().animatedUnzoom({ duration: 200 });
    },
    resetView() {
      this.sigmaRenderer?.getCamera().animatedReset({ duration: 200 });
    },
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        this.$refs.graphCard?.requestFullscreen();
      }
    },
    // Draggable divider between the canvas and the side panel. Shrinking the
    // panel grows the canvas (it's flex-1) — the ResizeObserver already
    // wired up in renderSigma() picks that up on every move and keeps
    // Sigma's canvas in sync, same as any other CSS-driven size change.
    startPanelResize(event) {
      event.preventDefault();
      const startX = event.clientX;
      const startWidth = this.panelWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const onMouseMove = moveEvent => {
        const delta = startX - moveEvent.clientX;
        this.panelWidth = Math.min(560, Math.max(240, startWidth + delta));
      };
      const onMouseUp = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    handleFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    destroyRenderer() {
      this.draggedNode = null;
      if (this.handleDragEnd) {
        document.removeEventListener('mouseup', this.handleDragEnd);
        this.handleDragEnd = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.sigmaRenderer) {
        this.sigmaRenderer.kill();
        this.sigmaRenderer = null;
      }
      this.graph = null;
    },
    handleSearch() {
      this.loadGraph(this.searchQuery.trim() || '*');
    },
    clearSearch() {
      this.searchQuery = '';
      this.loadGraph('*');
    },
    toggleTypeFilter(type) {
      const index = this.dimmedTypes.indexOf(type);
      if (index === -1) {
        this.dimmedTypes.push(type);
      } else {
        this.dimmedTypes.splice(index, 1);
      }
      this.refreshNodeColors();
    },
    // Client-side filter over the already-loaded graph — distinct from the
    // main search box, which re-queries the server to recenter on a new
    // root entity. This only dims/highlights nodes already in view.
    handleFindInView() {
      this.refreshNodeColors();
    },
    refreshNodeColors() {
      if (!this.graph) return;
      const query = this.findInViewQuery.trim().toLowerCase();
      const dimmedColor = isBodyInDarkMode()
        ? DIMMED_COLOR_DARK
        : DIMMED_COLOR_LIGHT;
      this.graph.forEachNode((nodeId, attrs) => {
        const typeDimmed = this.dimmedTypes.includes(attrs.entityType);
        const searchDimmed =
          !!query && !(attrs.label || '').toLowerCase().includes(query);
        const isDimmed = typeDimmed || searchDimmed;
        this.graph.setNodeAttribute(
          nodeId,
          'color',
          isDimmed ? dimmedColor : colorForType(attrs.entityType)
        );
      });
    },
    focusOnNeighbor(label) {
      this.searchQuery = label;
      this.loadGraph(label);
    },
  },
  async mounted() {
    // Defense in depth: the Graph tab is already hidden for KBs that are
    // non-lightrag or have graph support disabled, but a direct URL visit
    // must not fire the graph request either. Fetch dataset details
    // independently rather than trusting the parent ViewKnowledge shell to
    // have already populated it — Vue mounts child route components before
    // the parent's own mounted() hook runs, so on a direct link there's no
    // guarantee the parent's fetch has resolved yet.
    try {
      await this.fetchDatasetDetails(this.datasetId);
    } catch (err) {
      // fetchDatasetDetails rethrows on failure — without this, the
      // rejection would leave isLoading stuck true forever instead of
      // reaching the generic error state with its retry control.
      this.errorType = 'generic';
      this.isLoading = false;
      console.error('Error loading knowledge graph:', err);
      return;
    }
    if (this.knowledgeDetails.engine !== 'lightrag') {
      this.errorType = 'not-lightrag';
      this.isLoading = false;
      return;
    }
    if (!this.knowledgeDetails.graph_enabled) {
      this.errorType = 'graph-disabled';
      this.isLoading = false;
      return;
    }
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    await this.loadGraph('*');
  },
  beforeUnmount() {
    document.removeEventListener(
      'fullscreenchange',
      this.handleFullscreenChange
    );
    this.destroyRenderer();
  },
};
</script>

<template>
  <div
    class="p-4 md:p-8 flex flex-1 h-full flex-col m-0 overflow-auto bg-slate-50 dark:bg-slate-900 transition-colors duration-200"
  >
    <!-- Not a lightrag KB -->
    <div
      v-if="errorType === 'not-lightrag'"
      class="flex-1 flex items-center justify-center"
      data-testid="graph_not_lightrag_error"
    >
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('KNOWLEDGE.GRAPH.NOT_LIGHTRAG_ERROR') }}
      </p>
    </div>

    <!-- LightRAG KB without graph support enabled -->
    <div
      v-else-if="errorType === 'graph-disabled'"
      class="flex-1 flex items-center justify-center"
      data-testid="graph_disabled_error"
    >
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('KNOWLEDGE.GRAPH.GRAPH_DISABLED_ERROR') }}
      </p>
    </div>

    <!-- Not found -->
    <div
      v-else-if="errorType === 'not-found'"
      class="flex-1 flex items-center justify-center"
      data-testid="graph_not_found_error"
    >
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('KNOWLEDGE.GRAPH.NOT_FOUND_ERROR') }}
      </p>
    </div>

    <!-- Generic error -->
    <div
      v-else-if="errorType === 'generic'"
      class="flex-1 flex flex-col items-center justify-center gap-3"
      data-testid="graph_generic_error"
    >
      <p class="text-slate-600 dark:text-slate-400">
        {{ $t('KNOWLEDGE.GRAPH.LOAD_ERROR') }}
      </p>
      <woot-button
        variant="smooth"
        color-scheme="primary"
        icon="arrow-clockwise"
        data-testid="graph_retry_button"
        @click="loadGraph(searchQuery)"
      >
        {{ $t('KNOWLEDGE.VIEW_CHUNKS.TRY_AGAIN') }}
      </woot-button>
    </div>

    <template v-else>
      <!-- Controls -->
      <div class="flex-shrink-0 flex flex-col gap-3 mb-3">
        <div class="flex flex-wrap items-center gap-3">
          <!-- Query controls: everything here re-fetches the graph -->
          <div class="flex flex-wrap items-center gap-3 flex-1 min-w-0">
            <div class="relative flex-grow max-w-md min-w-[180px]">
              <fluent-icon
                icon="search"
                class="absolute top-3 left-3 h-4 w-4 text-slate-400"
                aria-hidden="true"
              />
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="$t('KNOWLEDGE.GRAPH.SEARCH_PLACEHOLDER')"
                class="reset-base pl-9 pr-8 py-2 w-full border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500 focus:border-woot-500 transition-colors"
                aria-label="Search graph entities"
                data-testid="graph_search_input"
                @keyup.enter="handleSearch"
              />
              <button
                v-if="searchQuery"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
                aria-label="Clear search"
                data-testid="graph_search_clear_button"
                @click="clearSearch"
              >
                <fluent-icon
                  icon="dismiss"
                  class="h-4 w-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                />
              </button>
            </div>
            <woot-button
              variant="smooth"
              color-scheme="secondary"
              size="small"
              class="flex-shrink-0"
              data-testid="graph_search_button"
              @click="handleSearch"
            >
              {{ $t('KNOWLEDGE.GRAPH.SEARCH_BUTTON') }}
            </woot-button>
            <div class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 flex-shrink-0">
              <label for="graph-depth">{{ $t('KNOWLEDGE.GRAPH.DEPTH_LABEL', { depth }) }}</label>
              <input
                id="graph-depth"
                v-model.number="depth"
                type="range"
                min="1"
                max="5"
                class="w-24"
                data-testid="graph_depth_slider"
              />
            </div>
            <div class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 flex-shrink-0">
              <label for="graph-max-nodes">{{ $t('KNOWLEDGE.GRAPH.MAX_NODES_LABEL') }}</label>
              <input
                id="graph-max-nodes"
                v-model.number="maxNodes"
                type="number"
                min="1"
                max="10000"
                class="reset-base w-20 py-1 px-2 text-sm border border-slate-300 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800"
                data-testid="graph_max_nodes_input"
              />
            </div>
          </div>

          <!-- View control: filters the already-loaded graph, no re-fetch -->
          <div class="flex items-center gap-2 flex-shrink-0 sm:pl-3 sm:border-l border-slate-200 dark:border-slate-700">
            <fluent-icon
              icon="filter"
              class="h-4 w-4 text-slate-400 flex-shrink-0"
              aria-hidden="true"
            />
            <div class="relative w-full sm:w-56">
              <input
                v-model="findInViewQuery"
                type="text"
                :placeholder="$t('KNOWLEDGE.GRAPH.FIND_IN_VIEW_PLACEHOLDER')"
                class="reset-base px-3 py-2 w-full border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-200 bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500 focus:border-woot-500 transition-colors"
                aria-label="Find an entity in the current view"
                data-testid="graph_find_in_view_input"
                @input="handleFindInView"
              />
            </div>
          </div>
        </div>

        <div
          v-if="isTruncated"
          class="flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-300 self-start"
          data-testid="graph_truncated_warning"
        >
          <fluent-icon icon="warning" class="h-4 w-4" />
          {{ $t('KNOWLEDGE.GRAPH.TRUNCATED_WARNING', { shown: nodes.length }) }}
        </div>
      </div>

      <!-- Loading state -->
      <div
        v-if="isLoading"
        class="flex-1 flex items-center justify-center"
        data-testid="graph_loading_state"
      >
        <LoadingState :message="$t('KNOWLEDGE.GRAPH.LOADING_STATE')" />
      </div>

      <!-- Empty graph -->
      <div
        v-else-if="nodes.length === 0"
        class="flex-1 flex items-center justify-center"
        data-testid="graph_empty_state"
      >
        <p class="text-slate-600 dark:text-slate-400">
          {{ $t('KNOWLEDGE.GRAPH.EMPTY_STATE') }}
        </p>
      </div>

      <div v-else class="flex-1 flex">
        <!-- Graph canvas + legend -->
        <div
          ref="graphCard"
          class="flex-1 min-w-0 min-h-[480px] relative rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden"
          data-testid="graph_card"
        >
          <div
            ref="sigmaContainer"
            class="relative w-full h-full"
            data-testid="graph_canvas"
          />

          <!-- Toolbar -->
          <div
            class="absolute top-3 left-3 flex flex-col gap-1 bg-white/90 dark:bg-slate-800/90 backdrop-blur rounded-md p-1 shadow-sm"
          >
            <button
              class="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              :title="$t('KNOWLEDGE.GRAPH.ZOOM_IN')"
              data-testid="graph_zoom_in_button"
              @click="zoomIn"
            >
              <fluent-icon icon="zoom-in" class="h-4 w-4 text-slate-600 dark:text-slate-300" />
            </button>
            <button
              class="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              :title="$t('KNOWLEDGE.GRAPH.ZOOM_OUT')"
              data-testid="graph_zoom_out_button"
              @click="zoomOut"
            >
              <fluent-icon icon="zoom-out" class="h-4 w-4 text-slate-600 dark:text-slate-300" />
            </button>
            <button
              class="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              :title="$t('KNOWLEDGE.GRAPH.RESET_VIEW')"
              data-testid="graph_reset_view_button"
              @click="resetView"
            >
              <fluent-icon icon="arrow-clockwise" class="h-4 w-4 text-slate-600 dark:text-slate-300" />
            </button>
            <button
              class="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
              :title="$t('KNOWLEDGE.GRAPH.TOGGLE_FULLSCREEN')"
              data-testid="graph_fullscreen_button"
              @click="toggleFullscreen"
            >
              <fluent-icon
                :icon="isFullscreen ? 'arrow-minimize' : 'arrow-maximize'"
                class="h-4 w-4 text-slate-600 dark:text-slate-300"
              />
            </button>
          </div>

          <!-- Status bar -->
          <div
            class="absolute bottom-3 right-3 bg-white/90 dark:bg-slate-800/90 backdrop-blur rounded-md px-2 py-1 shadow-sm text-xs text-slate-500 dark:text-slate-400"
            data-testid="graph_status_bar"
          >
            {{ $t('KNOWLEDGE.GRAPH.STATUS_BAR', { nodes: nodes.length, edges: edges.length, depth }) }}
          </div>

          <!-- Legend -->
          <div
            class="absolute bottom-3 left-3 bg-white/90 dark:bg-slate-800/90 backdrop-blur rounded-md p-2 shadow-sm max-w-[220px] max-h-[50%] overflow-y-auto"
            data-testid="graph_legend"
          >
            <p
              class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"
            >
              {{ $t('KNOWLEDGE.GRAPH.LEGEND_TITLE') }}
            </p>
            <button
              v-for="entry in legend"
              :key="entry.type"
              class="flex items-center gap-2 w-full text-left px-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-xs"
              :class="{ 'opacity-40': dimmedTypes.includes(entry.type) }"
              data-testid="graph_legend_item"
              :data-type="entry.type"
              :data-dimmed="dimmedTypes.includes(entry.type)"
              @click="toggleTypeFilter(entry.type)"
            >
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                :style="{ backgroundColor: entry.color }"
              />
              <span class="truncate flex-1 text-slate-700 dark:text-slate-200">
                {{ entry.type }}
              </span>
              <span class="text-slate-400">{{ entry.count }}</span>
            </button>
          </div>
        </div>

        <!-- Resize handle -->
        <div
          class="hidden md:flex flex-shrink-0 w-4 cursor-col-resize items-center justify-center group"
          data-testid="graph_resize_handle"
          @mousedown="startPanelResize"
        >
          <div
            class="w-1 h-10 rounded-full bg-slate-200 dark:bg-slate-700 group-hover:bg-woot-500 transition-colors"
          />
        </div>

        <!-- Side panel: always visible, shows an empty-state hint until a node is clicked -->
        <div
          class="hidden md:flex md:flex-col flex-shrink-0 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4"
          :style="{ width: panelWidth + 'px' }"
          data-testid="graph_side_panel"
        >
          <div
            v-if="!selectedNode"
            class="flex-1 flex items-center justify-center text-center"
            data-testid="graph_side_panel_hint"
          >
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ $t('KNOWLEDGE.GRAPH.SELECT_NODE_HINT') }}
            </p>
          </div>
          <template v-else>
            <div class="flex items-start justify-between mb-2">
              <h2
                class="font-bold text-sm"
                data-testid="graph_side_panel_label"
              >
                {{ selectedNode.label }}
              </h2>
              <button
                aria-label="Close"
                data-testid="graph_side_panel_close_button"
                @click="selectedNodeId = null"
              >
                <fluent-icon icon="dismiss" class="h-4 w-4 text-slate-400" />
              </button>
            </div>
            <span
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 mb-3"
            >
              {{ selectedNode.type }}
            </span>

            <div v-if="selectedNode.descriptionLines.length" class="mb-3">
              <p
                class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"
              >
                {{ $t('KNOWLEDGE.GRAPH.DESCRIPTION_TITLE') }}
              </p>
              <ul class="list-disc list-inside space-y-1">
                <li
                  v-for="(line, idx) in selectedNode.descriptionLines"
                  :key="idx"
                  class="text-xs text-slate-700 dark:text-slate-300"
                >
                  {{ line }}
                </li>
              </ul>
            </div>

            <div v-if="selectedNode.neighbors.length" class="mb-3">
              <p
                class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"
              >
                {{ $t('KNOWLEDGE.GRAPH.NEIGHBORS_TITLE') }} ({{
                  selectedNode.neighbors.length
                }})
              </p>
              <div class="flex flex-wrap gap-1">
                <button
                  v-for="neighbor in selectedNode.neighbors"
                  :key="neighbor"
                  class="px-2 py-0.5 rounded-full text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-woot-100 dark:hover:bg-woot-900/30"
                  data-testid="graph_neighbor_chip"
                  @click="focusOnNeighbor(neighbor)"
                >
                  {{ neighbor }}
                </button>
              </div>
            </div>

            <div v-if="selectedNode.sourceIds.length">
              <p
                class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"
              >
                {{ $t('KNOWLEDGE.GRAPH.SOURCES_TITLE') }}
              </p>
              <ul class="space-y-1">
                <li
                  v-for="sourceId in selectedNode.sourceIds"
                  :key="sourceId"
                  class="text-xs text-slate-500 dark:text-slate-400 truncate"
                >
                  {{ sourceId }}
                </li>
              </ul>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
